/**
 * Logger Utility
 * Centralized logging with environment-aware behavior.
 */

/**
 * Log levels for the logger.
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Logger configuration.
 */
interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  prefix: string;
}

/**
 * Log level priority (lower = more verbose).
 */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Default logger configuration.
 */
const defaultConfig: LoggerConfig = {
  enabled: import.meta.env.DEV,
  level: import.meta.env.DEV ? 'debug' : 'warn',
  prefix: '[DreamSystem]',
};

/**
 * Current logger configuration.
 */
let config: LoggerConfig = { ...defaultConfig };

/**
 * Configures the logger.
 */
export const configureLogger = (newConfig: Partial<LoggerConfig>): void => {
  config = { ...config, ...newConfig };
};

/**
 * Checks if a log level should be logged.
 */
const shouldLog = (level: LogLevel): boolean => {
  if (!config.enabled) return false;
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[config.level];
};

/**
 * Formats a log message.
 */
const formatMessage = (level: LogLevel, message: string): string => {
  const timestamp = new Date().toISOString();
  return `${config.prefix} [${timestamp}] [${level.toUpperCase()}] ${message}`;
};

/**
 * Sanitizes data for logging (removes sensitive information).
 */
const sanitizeData = (data: unknown): unknown => {
  if (data === null || data === undefined) return data;
  
  if (typeof data !== 'object') return data;
  
  if (Array.isArray(data)) {
    return data.map(sanitizeData);
  }
  
  const sanitized: Record<string, unknown> = {};
  const sensitiveKeys = [
    'password',
    'token',
    'accessToken',
    'refreshToken',
    'secret',
    'apiKey',
    'authorization',
    'cookie',
    'icNumber',
    'accountNumber',
  ];
  
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive.toLowerCase()))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * Logger object with methods for each log level.
 */
export const logger = {
  /**
   * Debug level logging (development only).
   */
  debug: (message: string, ...data: unknown[]): void => {
    if (!shouldLog('debug')) return;
    const sanitizedData = data.map(sanitizeData);
    console.debug(formatMessage('debug', message), ...sanitizedData);
  },

  /**
   * Info level logging.
   */
  info: (message: string, ...data: unknown[]): void => {
    if (!shouldLog('info')) return;
    const sanitizedData = data.map(sanitizeData);
    console.info(formatMessage('info', message), ...sanitizedData);
  },

  /**
   * Warning level logging.
   */
  warn: (message: string, ...data: unknown[]): void => {
    if (!shouldLog('warn')) return;
    const sanitizedData = data.map(sanitizeData);
    console.warn(formatMessage('warn', message), ...sanitizedData);
  },

  /**
   * Error level logging.
   */
  error: (message: string, error?: unknown, ...data: unknown[]): void => {
    if (!shouldLog('error')) return;
    const sanitizedData = data.map(sanitizeData);
    
    if (error instanceof Error) {
      console.error(
        formatMessage('error', message),
        {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
        ...sanitizedData
      );
    } else if (error !== undefined) {
      console.error(formatMessage('error', message), sanitizeData(error), ...sanitizedData);
    } else {
      console.error(formatMessage('error', message), ...sanitizedData);
    }
  },

  /**
   * Groups related log messages.
   */
  group: (label: string, fn: () => void): void => {
    if (!config.enabled) return;
    console.group(formatMessage('info', label));
    fn();
    console.groupEnd();
  },

  /**
   * Logs a table of data.
   */
  table: (data: unknown[], columns?: string[]): void => {
    if (!shouldLog('debug')) return;
    console.table(data, columns);
  },

  /**
   * Times an operation.
   */
  time: (label: string): void => {
    if (!config.enabled) return;
    console.time(`${config.prefix} ${label}`);
  },

  /**
   * Ends timing an operation.
   */
  timeEnd: (label: string): void => {
    if (!config.enabled) return;
    console.timeEnd(`${config.prefix} ${label}`);
  },
};

/**
 * Creates a scoped logger with a custom prefix.
 */
export const createScopedLogger = (scope: string) => {
  const scopedPrefix = `${config.prefix}[${scope}]`;
  
  return {
    debug: (message: string, ...data: unknown[]) => {
      if (!shouldLog('debug')) return;
      const sanitizedData = data.map(sanitizeData);
      console.debug(`${scopedPrefix} [DEBUG] ${message}`, ...sanitizedData);
    },
    info: (message: string, ...data: unknown[]) => {
      if (!shouldLog('info')) return;
      const sanitizedData = data.map(sanitizeData);
      console.info(`${scopedPrefix} [INFO] ${message}`, ...sanitizedData);
    },
    warn: (message: string, ...data: unknown[]) => {
      if (!shouldLog('warn')) return;
      const sanitizedData = data.map(sanitizeData);
      console.warn(`${scopedPrefix} [WARN] ${message}`, ...sanitizedData);
    },
    error: (message: string, error?: unknown, ...data: unknown[]) => {
      if (!shouldLog('error')) return;
      const sanitizedData = data.map(sanitizeData);
      console.error(`${scopedPrefix} [ERROR] ${message}`, error, ...sanitizedData);
    },
  };
};

export default logger;

